import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Alert,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const RoleSelection = ({ onRoleSelect, onBack }) => {
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);

  const capabilities = [
    {
      id: 'job_seeker',
      title: 'Job Seeker',
      description: 'Looking for employment opportunities and career growth',
      icon: <PersonIcon sx={{ fontSize: 40 }} />,
  color: '#cf1130',
      features: ['Upload CV', 'Apply to Jobs', 'Track Applications', 'Skill Assessment']
    },
    {
      id: 'employer',
      title: 'Employer',
      description: 'Hiring talent and managing recruitment',
      icon: <BusinessIcon sx={{ fontSize: 40 }} />,
      color: '#4caf50',
      features: ['Post Jobs', 'Manage Applications', 'Company Profile', 'Analytics']
    }
  ];

  const handleCapabilityToggle = (capabilityId) => {
    setSelectedCapabilities(prev => {
      if (prev.includes(capabilityId)) {
        return prev.filter(id => id !== capabilityId);
      } else {
        return [...prev, capabilityId];
      }
    });
  };

  const handleContinue = () => {
    if (selectedCapabilities.length > 0) {
      // Set the first selected capability as primary role
      const primaryRole = selectedCapabilities[0];
      onRoleSelect({
        primaryRole,
        capabilities: selectedCapabilities
      });
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Choose Your Capabilities
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select all the ways you plan to use our platform. You can switch between these capabilities anytime.
      </Typography>

      {selectedCapabilities.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Selected capabilities:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedCapabilities.map(capId => {
              const cap = capabilities.find(c => c.id === capId);
              return (
                <Chip
                  key={capId}
                  label={cap?.title}
                  size="small"
                  sx={{ bgcolor: cap?.color, color: 'white' }}
                />
              );
            })}
          </Box>
        </Alert>
      )}

      <Grid container spacing={3}>
        {capabilities.map((capability) => {
          const isSelected = selectedCapabilities.includes(capability.id);
          
          return (
            <Grid item xs={12} sm={6} key={capability.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  },
                  border: `2px solid ${isSelected ? capability.color : 'transparent'}`,
                  bgcolor: isSelected ? `${capability.color}08` : 'background.paper',
                  position: 'relative'
                }}
                onClick={() => handleCapabilityToggle(capability.id)}
              >
                {isSelected && (
                  <CheckCircleIcon
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      color: capability.color,
                      fontSize: 24
                    }}
                  />
                )}
                
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: capability.color,
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      opacity: isSelected ? 1 : 0.7
                    }}
                  >
                    {capability.icon}
                  </Avatar>
                  
                  <Typography variant="h6" gutterBottom>
                    {capability.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {capability.description}
                  </Typography>
                  
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: capability.color }}>
                      Key Features:
                    </Typography>
                    {capability.features.map((feature, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • {feature}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={onBack}>
          Back
        </Button>
        <Button 
          variant="contained" 
          onClick={handleContinue}
          disabled={selectedCapabilities.length === 0}
        >
          Continue ({selectedCapabilities.length} selected)
        </Button>
      </Box>
    </Box>
  );
};

export default RoleSelection;